import { useState } from "react";
import { format, parseISO } from "date-fns";
import { it, hr, enGB, de, hu, cs, sl } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useApp } from "@/context/AppContext";

const LOCALES = { it, hr, en: enGB, de, hu, cs, sl };

const toDate = (s) => (s ? parseISO(s) : undefined);
const toStr = (d) => (d ? format(d, "yyyy-MM-dd") : "");

export const DateRangePicker = ({ ci, co, onChange, triggerClassName = "", light = false }) => {
  const { t, lang } = useApp();
  const [open, setOpen] = useState(false);
  const locale = LOCALES[lang] || enGB;
  const range = { from: toDate(ci), to: toDate(co) };

  const label = ci
    ? `${format(toDate(ci), "d MMM", { locale })}${co ? " – " + format(toDate(co), "d MMM", { locale }) : ""}`
    : t("search.select_dates");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="date-range-trigger"
          className={triggerClassName || "flex items-center gap-3 rounded-xl bg-white/70 px-4 py-3 text-left"}
        >
          <CalendarDays size={18} className="text-terracotta" />
          <span className="flex flex-col">
            <span className="overline text-petrol/50" style={{ fontSize: "0.6rem" }}>
              {t("search.dates")}
            </span>
            <span className="text-sm font-semibold text-petrol">{label}</span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto bg-sand-card p-0" data-testid="date-range-popover">
        <Calendar
          mode="range"
          numberOfMonths={2}
          selected={range}
          defaultMonth={range.from || new Date()}
          locale={locale}
          disabled={{ before: new Date() }}
          onSelect={(r) => {
            onChange(toStr(r?.from), toStr(r?.to));
            if (r?.from && r?.to) setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
